export interface WebsiteChangeSetActionResult {
  changeSetId: string
  status: 'building' | 'ready' | 'applied' | 'rejected' | 'stale' | 'failed' | 'expired'
  baseRevision: number
  appliedRevision?: number | null
  revision: number
  mutated: boolean
  html?: string | null
  renderHtml?: string | null
  siteId?: string
  warnings?: unknown[]
  error?: string
}

export class WebsiteBuilderService {
  constructor(private readonly baseUrl: string, private readonly token: string | null) {}

  async approvePlan(sessionId: string): Promise<void> {
    const headers: Record<string, string> = {'Content-Type': 'application/json'}
    if (this.token) headers.Authorization = `Bearer ${this.token}`
    const response = await fetch(`${this.baseUrl}/api/site-builder/tool`, {
      method: 'POST', credentials: 'include', headers,
      body: JSON.stringify({sessionId, tool: 'approve_plan', arguments: {}}),
    })
    const body = await response.json().catch(() => ({})) as {error?: string; structuredContent?: {error?: string}}
    if (!response.ok || body.error || body.structuredContent?.error) {
      throw new Error(body.error || body.structuredContent?.error || `Plan approval failed (${response.status})`)
    }
  }

  private async tool(sessionId: string, tool: string, args: Record<string, unknown>): Promise<WebsiteChangeSetActionResult> {
    const headers: Record<string, string> = {'Content-Type': 'application/json'}
    if (this.token) headers.Authorization = `Bearer ${this.token}`
    const response = await fetch(`${this.baseUrl}/api/site-builder/tool`, {
      method: 'POST', credentials: 'include', headers,
      body: JSON.stringify({sessionId, tool, arguments: args}),
    })
    const body = await response.json() as {structuredContent?: WebsiteChangeSetActionResult; error?: string}
    if (!response.ok || body.error || body.structuredContent?.error) {
      throw new Error(body.error || body.structuredContent?.error || `Website action failed (${response.status})`)
    }
    if (!body.structuredContent) throw new Error('Website action returned no state.')
    return body.structuredContent
  }

  approveChangeSet(sessionId: string, changeSetId: string) {
    return this.tool(sessionId, 'approve_change_set', {changeSetId, summary: 'Approved website changes'})
  }

  rejectChangeSet(sessionId: string, changeSetId: string) {
    return this.tool(sessionId, 'reject_change_set', {changeSetId})
  }

  reopenChangeSet(sessionId: string, changeSetId: string) {
    return this.tool(sessionId, 'reopen_change_set', {changeSetId})
  }

  async publishRevision(sessionId: string, expectedRevision: number): Promise<{
    siteId: string | number
    sourceRevision: number
    liveUrl: string
    versionUrl: string
    version: number
  }> {
    const headers: Record<string, string> = {'Content-Type': 'application/json'}
    if (this.token) headers.Authorization = `Bearer ${this.token}`
    const response = await fetch(`${this.baseUrl}/api/r3/website/publish`, {
      method: 'POST', credentials: 'include', headers,
      body: JSON.stringify({sessionId, expectedRevision}),
    })
    const body = await response.json() as {siteId?: string | number; sourceRevision?: number; liveUrl?: string; versionUrl?: string; version?: number; error?: string}
    if (!response.ok || body.error || body.siteId === undefined || !body.liveUrl || !body.versionUrl || body.version === undefined) {
      throw new Error(body.error || `Publishing failed (${response.status})`)
    }
    return body as {siteId: string | number; sourceRevision: number; liveUrl: string; versionUrl: string; version: number}
  }
}
